import React from 'react';
import 'twin.macro';
import {
  Matiere as IMatiere,
  Periode,
} from '@teetsh/app/src/types/programmations';
import { LabelledDateRange } from '@teetsh/app/src/types/zone';
import { PrintView, RowTitleOrientation } from './types';
import { chunk } from 'lodash-es';
import { groupWeeksByPeriode } from '@teetsh/app/src/containers/Programmations/ProgV2/utils/groupWeeksByPeriodes';
import { DEFAULT_NB_OF_WEEKS_PER_LINE } from '@teetsh/app/src/containers/Programmations/ProgV2/constants';
import { PeriodeMatiereView } from './PeriodeMatiereView';
import { MatierePeriodeView } from './MatierePeriodeView';

interface Props {
  matieres: IMatiere[];
  periodes: Periode[];
  view: Extract<
    PrintView,
    'periode_x_matiere' | 'matiere_x_periode' | 'matiere_x_semaine'
  >;
  schoolyearPeriodes: LabelledDateRange[];
  hideMatiereTitle?: boolean;
  groupWeeksByPeriodes?: boolean;
  rowTitleOrientation: RowTitleOrientation;
  preventCutting: boolean;
  breakBefore: boolean;
}

export function MatieresUnified(props: Props) {
  const { matieres, periodes, view, schoolyearPeriodes } = props;
  const { weeksByPeriodes: weeksByPeriodesFound = [], otherWeeks = [] } =
    groupWeeksByPeriode(periodes, schoolyearPeriodes);

  return (
    <div tw="flex flex-col gap-y-8">
      {(() => {
        switch (view) {
          case 'periode_x_matiere': {
            if (!props.groupWeeksByPeriodes) {
              // Si on ne group pas par période, on limite simplement le nombre de semaines par ligne
              const groups = chunk(periodes, DEFAULT_NB_OF_WEEKS_PER_LINE);
              return groups.map((group, i) => (
                <PeriodeMatiereView
                  preventCutting={props.preventCutting}
                  breakBefore={props.breakBefore}
                  key={i}
                  isFirst={i === 0}
                  periodes={group}
                  matieres={matieres}
                  rowTitleOrientation={props.rowTitleOrientation}
                  enforceNbColumns={
                    groups.length > 1 ? DEFAULT_NB_OF_WEEKS_PER_LINE : undefined
                  }
                />
              ));
            }

            const groups = weeksByPeriodesFound.flatMap(g => {
              const grps = chunk(g.weeks, DEFAULT_NB_OF_WEEKS_PER_LINE);
              return grps.map(group => ({
                weeks: group,
                periode: g.periode,
              }));
            });
            const otherGroups = chunk(
              otherWeeks,
              DEFAULT_NB_OF_WEEKS_PER_LINE,
            ).map(c => ({
              weeks: c,
              periode: {
                id: 'other',
                label: '?',
              },
            }));

            const allGroups = [...groups, ...otherGroups];
            return allGroups.map((group, i) => (
              <PeriodeMatiereView
                preventCutting={props.preventCutting}
                breakBefore={props.breakBefore}
                key={i}
                isFirst={i === 0}
                periodeLabel={group.periode?.label}
                periodes={group.weeks}
                matieres={matieres}
                rowTitleOrientation={props.rowTitleOrientation}
                enforceNbColumns={
                  groups.length > 1 ? DEFAULT_NB_OF_WEEKS_PER_LINE : undefined
                }
              />
            ));
          }
          case 'matiere_x_periode': {
            if (!props.groupWeeksByPeriodes) {
              const groups = chunk(matieres, DEFAULT_NB_OF_WEEKS_PER_LINE);
              return groups.map((group, i) => (
                <MatierePeriodeView
                  preventCutting={props.preventCutting}
                  breakBefore={props.breakBefore}
                  key={i}
                  isFirst={i === 0}
                  matieres={group}
                  periodes={periodes}
                  rowTitleOrientation={props.rowTitleOrientation}
                  enforceNbColumns={
                    groups.length > 1 ? DEFAULT_NB_OF_WEEKS_PER_LINE : undefined
                  }
                />
              ));
            }

            const groups = chunk(matieres, DEFAULT_NB_OF_WEEKS_PER_LINE);
            return groups.map((group, i) => (
              <React.Fragment key={i}>
                {weeksByPeriodesFound.map((p, ii) => (
                  <MatierePeriodeView
                    preventCutting={props.preventCutting}
                    breakBefore={props.breakBefore}
                    isFirst={ii === 0}
                    key={`${i}-${ii}`}
                    matieres={group}
                    periodes={p.weeks}
                    rowTitleOrientation={props.rowTitleOrientation}
                    enforceNbColumns={
                      groups.length > 1
                        ? DEFAULT_NB_OF_WEEKS_PER_LINE
                        : undefined
                    }
                  />
                ))}
              </React.Fragment>
            ));
          }
        }
      })()}
    </div>
  );
}
